<template>
    <ProviderButton
        id="google"
        :color="props.color"
        :size="props.size"
        providerDisplayName="Google"
        :disabled="pending"
        :onClick="handleClick"
    />
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from 'vue'

import { useConfig } from '@/composables/config'
import { useAppStore } from '@/store/app-store'
import { useNativeAppStore } from '@/store/native-app-store'
import { useUserStore } from '@/store/user-store/user-store'
import { generateNonce } from '@/utils/crypto'

import ProviderButton from './ProviderButton.vue'

const props = defineProps<{
    color: 'carbon-400' | 'carbon-200'
    size: 's' | 'l'
    onFinish?: (isSuccess: boolean, errorMessage?: string) => void
}>()

const { VUE_APP_GAUTH_REDIRECT, VUE_APP_GAUTH_CLID, VUE_APP_IOS_GAUTH_CLID } = useConfig()
const nativeAppStore = useNativeAppStore()
const appStore = useAppStore()
const userStore = useUserStore()

const pending = ref(false)
const webViewRawNonce = ref<string>()
const unsubscribe = ref<() => void>()

async function handleClick() {
    if (pending.value) {
        return
    }

    pending.value = true

    try {
        const { rawNonce } = await generateNonce()
        webViewRawNonce.value = rawNonce
        nativeAppStore.postMessage('googleAuth.event', {
            nonce: rawNonce,
            clid: VUE_APP_GAUTH_CLID,
            iosClid: appStore.isNativeIosEnv ? VUE_APP_IOS_GAUTH_CLID : undefined,
        })
    } catch (e) {
        props.onFinish?.(false, (e as Error)?.message || String(e))
    } finally {
        pending.value = false
    }
}

// second argument for hashedNonce from native app - right now we not using but it can be useful in the future
async function handleAuthentication(credential: string, _?: string) {
    let errorMessage: string | undefined

    try {
        const res = await fetch(VUE_APP_GAUTH_REDIRECT, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ credential, nonce: webViewRawNonce.value }),
        })
        if (res.status > 399) {
            const parsed = await res.json()
            errorMessage = parsed.message
            if (appStore.isDebugMode) {
                console.error(`handleMessage from SUPABASE ${JSON.stringify(parsed)}`, 'supabase error')
                errorMessage = JSON.stringify(parsed)
            }
        }
    } catch (e) {
        errorMessage = (e as Error)?.message || String(e)
        if (appStore.isDebugMode) {
            console.error(
                `handleMessage from SUPABASE ${JSON.stringify((e as Error)?.message || String(e))}`,
                'supabase error catch block',
            )
            errorMessage = `handleMessage from SUPABASE ${JSON.stringify((e as Error)?.message || String(e))}`
        }
    }

    await userStore.fetchUserInfo()
    props.onFinish?.(userStore.isAuthorized, errorMessage)
}

function handleMessage(data: unknown) {
    if (!data || typeof data !== 'object') {
        return
    }

    const message = data as { type?: string; payload?: Record<string, unknown> }

    if (message.type !== 'native:auth.event' || !message.payload) {
        return
    }

    const { credential, errorCode } = message.payload
    if (typeof credential !== 'string') {
        if (appStore.isDebugMode) {
            console.error(JSON.stringify(message), 'handleMessage from NATIVE')
            props.onFinish?.(false, `handleMessage from NATIVE ${errorCode}: ${JSON.stringify(message) || ''}`)
        } else {
            props.onFinish?.(false, `Credential or nonce is missing in the authentication response ${errorCode || ''}`)
        }
        return
    }

    handleAuthentication(credential)
}

onMounted(() => {
    unsubscribe.value = nativeAppStore.addMessageHandler(handleMessage)
})

onUnmounted(() => {
    unsubscribe.value?.()
})
</script>
